<template>
  <div class="ui-order-item rounded-lg relative" :class="[$theme.cardBg]">
    <!-- overlay при фоновой задаче -->
    <div
        v-if="order.user_task != null"
        class="absolute inset-0 bg-opacity-60 rounded-xl flex items-center justify-center z-40"
        :class="[$theme.cardBg]"
    >
      <svg class="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
      </svg>
    </div>

    <!-- header -->
    <div class="flex px-2 pt-2 pb-2 border-b" :class="[$theme.cardBorderColor]">
      <div class="w-full flex items-center justify-between font-semibold whitespace-nowrap">
        <div class="flex items-center">
          <div>
            <div class="relative flex items-center">
              <CryptoIcon :symbol="order.symbol" />
              <template v-if="order.symbol?.includes('USDT')">
                <img src="../../../node_modules/cryptocurrency-icons/svg/color/usdt.svg" class="w-5 relative right-2">
              </template>
              <template v-if="order.symbol?.includes('USDC')">
                <img src="../../../node_modules/cryptocurrency-icons/svg/color/usdc.svg" class="w-5 relative right-2">
              </template>
              <span>{{ order.symbol }} x{{ parseInt(order.leverage) }}</span>
            </div>
          </div>
          <div class="pl-3">
            <div
                class="text-sm font-medium px-2 rounded-lg uppercase"
                :class="[order.side === 'long' ? 'bg-green5e/20 text-green5e' : 'bg-red/20 text-red']"
            >
              {{ order.side }}
            </div>
          </div>
        </div>

        <div v-if="is_edit === true" class="flex items-center font-normal text-sm">
          <Menu as="div" class="relative inline-block text-left">
            <div>
              <MenuButton class="flex items-center rounded-full focus:outline-none" :class="[$theme.baseBg, $theme.baseTextColor]">
                <span class="sr-only">Open options</span>
                <EllipsisVerticalIcon class="h-5 w-5 rounded-full" aria-hidden="true" />
              </MenuButton>
            </div>
            <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                  class="absolute right-0 z-10 w-52 mt-1 max-w-sm flex-auto rounded-xl text-sm leading-6 shadow-lg ring-1 border ring-gray-900/5"
                  :class="[$theme.baseBg, $theme.baseTextColor, $theme.borderOrange]"
              >
                <div class="py-1">
                  <MenuItem v-slot="{ active }">
                    <a @click="closePosition(order)" href="javascript:void(0)" :class="[active ? 'opacity-90' : 'opacity-90', 'block px-4 py-2 text-sm']">
                      {{ $t('close_position') }}
                    </a>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>

    <!-- summary -->
    <div class="grid grid-cols-3 px-2 pt-2 pb-2">
      <div class="whitespace-nowrap">
        <div class="text-xs" :class="[$theme.cardLabelTextColor]">
          {{ order.closed ? $t('in_position') : $t('margin') }}
        </div>
        <template v-if="order.closed">
          <div class="text-sm">
            {{ roundAmount(order.initial_margin || 0, 2) }} USDT
          </div>
        </template>
        <template v-else>
          <TheTooltip :id="`o_${order.id}`" :message="`${$t('in_position')} ${order.initial_margin || 0} USDT`">
            <div class="text-sm">{{ roundAmount(order.initial_margin || 0, 2) }} USDT</div>
          </TheTooltip>
        </template>
      </div>
      <div class="whitespace-nowrap">
        <div class="text-xs" :class="[$theme.cardLabelTextColor]">{{ $t('size') }}</div>
        <div class="text-sm mt-0.5" :class="[order.side === 'long' ? $theme.successPnlColor : $theme.dangerPnlColor]">
          {{ order.closed ? parseFloat(order.contracts || 0) : parseFloat(order.current_qty || 0) }} {{ (order.symbol || '').replace('USDT', '') }}
        </div>
      </div>
      <div class="whitespace-nowrap">
        <div class="text-xs" :class="[$theme.cardLabelTextColor]">P&L</div>
        <ShowPnl :pnl="order.unrealized_pnl || 0" :percent="order.percentage" />
      </div>
    </div>

    <!-- details + SIGNAL HISTORY -->
    <Disclosure as="div" class="w-full px-2 py-2 text-sm border-t" :class="[$theme.cardBorderColor]" v-slot="{ open }">
      <dt class="flex justify-between items-center w-full">
        <div class="flex items-center">
          <img :src="`/img/${order.exchange.exchange_slug}.png`" class="w-4 rounded-full mr-1">
          {{ order.exchange.title }} <span class="opacity-50 pl-2">ID: {{ order.exchange.id }}</span>
        </div>
        <DisclosureButton class="flex items-start text-left" :class="[$theme.cardLabelTextColor]">
          <span class="text-sm">{{ $t('detail') }}</span>
          <span class="ml-1 flex h-5 items-center">
            <PlusSmallIcon v-if="!open" class="h-5 w-5" aria-hidden="true" />
            <MinusSmallIcon v-else class="h-5 w-5" aria-hidden="true" />
          </span>
        </DisclosureButton>
      </dt>

      <DisclosurePanel as="dd" class="w-full pt-4">
        <!-- основные детали -->
        <div class="grid grid-cols-2 gap-y-2">
          <div>
            <div class="text-xs" :class="[$theme.cardLabelTextColor]">{{ $t('avg_price') }}</div>
            <div class="text-sm">{{ order.entry_price ?? '0' }}</div>
          </div>
          <div v-if="!order.closed">
            <div class="text-xs" :class="[$theme.cardLabelTextColor]">{{ $t('liquidation_price') }}</div>
            <div class="text-sm" :class="[$theme.dangerPnlColor]">
              {{ order.liquidation_price || '- -' }}
            </div>
          </div>
          <div v-if="!order.closed">
            <div class="text-xs" :class="[$theme.cardLabelTextColor]">TP/SL</div>
            <div class="text-sm">
              <template v-if="order.takeprofit_price">
                <span :class="[$theme.successPnlColor]">{{ order.takeprofit_price }}</span>
              </template>
              <template v-else><span>-</span></template>
              /
              <template v-if="order.stoploss_price">
                <span v-if="order.side === 'long'" :class="[$theme.dangerPnlColor]">
                  {{ parseFloat(order.stoploss_price) > 0.00001 ? order.stoploss_price : '≤0.00001' }}
                </span>
                <span v-if="order.side === 'short'" :class="[$theme.successPnlColor]">
                  {{ parseFloat(order.stoploss_price) < 999999 ? order.stoploss_price : '≥999999' }}
                </span>
              </template>
              <template v-else><span>-</span></template>
            </div>
          </div>
          <div>
            <div class="text-xs" :class="[$theme.cardLabelTextColor]">{{ $t('created_at') }}</div>
            <div class="text-sm">{{ order.created_at_f }}</div>
          </div>
          <div v-if="order.closed">
            <div class="text-xs" :class="[$theme.cardLabelTextColor]">{{ $t('closing_at') }}</div>
            <div class="text-sm">{{ order.closed_at_f || '- -' }}</div>
          </div>
          <div v-if="order.closed">
            <div class="text-xs" :class="[$theme.cardLabelTextColor]">{{ $t('closed_reason') }}</div>
            <div class="text-sm">{{ order.closed_reason || $t('manually') }}</div>
          </div>
        </div>

        <!-- divider -->
        <div class="my-4 h-px w-full" :class="[$theme.cardBorderColor]"></div>

        <!-- КОМПАКТНАЯ ИСТОРИЯ СИГНАЛОВ: ТИП • СТАТУС • ДАТА -->
        <div class="w-full">
          <div class="flex items-center justify-between mb-2">
            <div class="text-sm font-semibold">{{ $t('signal_logs') }}</div>
            <button
                type="button"
                class="text-xs px-3 py-1.5 rounded-lg transition"
                :class="[$theme.placeBg, $theme.cardLabelTextColor]"
                @click="historyOpen = !historyOpen"
            >
              {{ historyOpen ? $t('hide_more') : $t('details') }}
            </button>
          </div>

          <transition name="fade">
            <div v-if="historyOpen">
              <div v-if="!renderHistory.length" class="text-xs opacity-70">
                {{ $t('no_signals_found') || 'No signals in this position' }}
              </div>

              <div v-else class="space-y-1.5">
                <div
                    v-for="h in renderHistory"
                    :key="h.id"
                    class="px-3 py-2 rounded-lg flex items-center justify-between"
                    :class="[$theme.placeBg]"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <span class="text-[11px] font-semibold px-2 py-0.5 rounded-md uppercase" :class="badgeByType(h.type)">
                      {{ $t(h.type) }}
                    </span>
                    <!--<span v-if="h.status" class="text-[11px] px-2 py-0.5 rounded-md"
                          :class="statusBadge(h.status)">
                      {{ (h.status || '').toLowerCase() }}
                    </span>-->
                  </div>

                  <div class="shrink-0 text-[11px] opacity-70">
                    {{ h.created_at_format || h.created_at }}
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </DisclosurePanel>
    </Disclosure>
  </div>
</template>

<script>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { EllipsisVerticalIcon } from "@heroicons/vue/20/solid";
import CryptoIcon from "@/components/icons/CryptoIcon.vue";
import TheTooltip from "@/components/elem/TheTooltip.vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/vue/24/outline'
import ShowPnl from "@/components/elem/ShowPnl.vue";
import { roundAmount } from "@/helpers/numberHelper"

export default {
  props: ['order', 'is_edit'],

  components: {
    EllipsisVerticalIcon,
    CryptoIcon,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TheTooltip,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    MinusSmallIcon,
    PlusSmallIcon,
    ShowPnl
  },

  data() {
    return {
      roundAmount,
      historyOpen: true
    }
  },

  computed: {
    renderHistory() {
      const list = Array.isArray(this.order?.histories) ? this.order.histories : []
      const normalized = list.map(this.unifyFromHistory)
      return normalized.sort((a, b) =>
          new Date(a.created_at || a.created_at_format) - new Date(b.created_at || b.created_at_format)
      )
    }
  },

  methods: {
    closePosition(order) {
      this.$api.post(`/positions/${order.id}/close`).then(() => {
        this.$emit('close-position', 1);
      }).catch(e => {
        alert(e.message)
        this.error = e
      })
    },

    // нормализация хистори
    unifyFromHistory(h) {
      const log = h?.log || {}
      return {
        id: h.id,
        type: h.type || log.type,
        status: h.status || null,
        created_at: h.created_at || log.created_at,
        created_at_format: h.created_at_format || log.created_at_format
      }
    },

    // бейджи
    badgeByType(t) {
      const g = 'bg-green5e/20 text-green5e';
      const r = 'bg-red/20 text-red';
      const o = 'bg-orange/20 text-orange';
      switch (t) {
        case 'OPEN': return g
        case 'TAKEPROFIT': return g
        case 'EXIT': return r
        case 'STOPLOSS': return r
        case 'DCA': return o
        case 'CHANGE_STOPLOSS': return o
        default: return o
      }
    },
    statusBadge(s) {
      const map = {
        success: 'bg-green5e/15 text-green5e',
        failed: 'bg-red/15 text-red',
        ignored: 'bg-graya9/20 text-graya9'
      }
      return map[s] || 'bg-graya9/20 text-graya9'
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
